import React from 'react';
import PropTypes from 'prop-types';
import { DraggableCore } from 'react-draggable';
import throttle from 'lodash/throttle';
import noop from 'lodash/noop';

import './_ColumnResizer.scss';

const INVALID_VALUE = null;
const MIN_WIDTH = 16;
const THROTTLE_WAIT = 50;

/**
 * ColumnResizer for the Table
 */
class ColumnResizer extends React.PureComponent {
  constructor(props) {
    super(props);

    this.lastX = INVALID_VALUE;
    this.width = 0;

    this._handleDrag = this._handleDrag.bind(this);
    this._handleStart = this._handleStart.bind(this);
    this._handleStop = this._handleStop.bind(this);
    this._handleClick = this._handleClick.bind(this);
  }

  render() {
    const { style, width, disabled } = this.props;

    return (
      <DraggableCore
        axis="x"
        disabled={disabled}
        onStart={this._handleStart}
        onDrag={throttle(this._handleDrag, THROTTLE_WAIT)}
        onStop={this._handleStop}
      >
        <div className="ColumnResizer" onClick={this._handleClick}>
          <div className="ColumnResizer__handle" style={{ ...style, width }} />
        </div>
      </DraggableCore>
    );
  }

  _handleDrag(e, data) {
    if (this.lastX === INVALID_VALUE) {
      this.lastX = data.lastX;
      return true;
    }
    const movedX = data.x - this.lastX;
    if (!movedX) return true;
    this.lastX = data.x;
    this.width = this.width + movedX;

    const { column } = this.props;
    if (column.maxWidth && this.width >= column.maxWidth) return true;
    if (this.width <= (column.minWidth || MIN_WIDTH)) return true;

    return this.props.onResize(this.props.column, this.width);
  }

  _handleStart(e, data) {
    this.lastX = INVALID_VALUE;
    this.width = this.props.column.width;
    return this.props.onResizeStart(this.props.column);
  }

  _handleStop(e, data) {
    return this.props.onResizeStop(this.props.column);
  }

  _handleClick(e) {
    // click event activate the dragging event, `onResizeStart` and `onResizeStop` are called
    // so we stop event propagation to make clicking on resizing handler only responses to one behavior: dragging
    e.stopPropagation();
  }
}

ColumnResizer.defaultProps = {
  width: 3,
  onResizeStart: noop,
  onResize: noop,
  onResizeStop: noop
};

ColumnResizer.propTypes = {
  /**
   * The column object to be dragged
   */
  column: PropTypes.object,
  /**
   * The width of the drag handler
   */
  width: PropTypes.number.isRequired,
  /**
   * Whether the resizing is disabled
   */
  disabled: PropTypes.bool,
  /**
   * Custom style for the drag handler
   */
  style: PropTypes.object,
  /**
   * A callback function when resizing started
   * The callback is of the shape of "(column) => *"
   */
  onResizeStart: PropTypes.func,
  /**
   * A callback function when resizing the column
   * The callback is of the shape of "(column, width) => *"
   */
  onResize: PropTypes.func,
  /**
   * A callback function when resizing stopped
   * The callback is of the shape of "(column) => *"
   */
  onResizeStop: PropTypes.func
};

export default ColumnResizer;
